import {
  AskableAsset,
  AskableAssetFormData,
  AskableAssetResults,
  AskableAssetSearchParams,
  AskableImageParams,
} from "types";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AskablesAPI } from "api";

const queryKeys = {
  all: () => ["askables"] as const,
  askable: (id: number) => [...queryKeys.all(), id] as const,
  assets: (id: number) => [...queryKeys.askable(id), "assets"] as const,
  assetsWithParams: (params: AskableAssetSearchParams) => [...queryKeys.all(), params] as const,
  asset: (id: number) => [...queryKeys.assets(id)] as const,
  image: (id: number, params: AskableImageParams) => [...queryKeys.askable(id), "image", params] as const,
};

/**
 * Fetches all AskableAssets for the current account.
 */
export const useAskableAssetList = <T = AskableAssetResults>(
  params: AskableAssetSearchParams,
  options: UseQueryOptions<AskableAssetResults, unknown, T> = {},
) =>
  useQuery<AskableAssetResults, unknown, T>(
    queryKeys.assetsWithParams(params),
    () => AskablesAPI.listAssets(params),
    options,
  );

/**
 * Fetch an AskableAsset.
 */
export const useAskableAsset = <T extends AskableAsset>(
  id: number,
  options: UseQueryOptions<AskableAsset, unknown, T>,
) => useQuery<AskableAsset, unknown, T>(queryKeys.asset(id), () => AskablesAPI.getAsset(id), options);

/**
 * Mutation to create a new AskableAsset.
 */
export const useAskableAssetCreate = (
  askableId: number,
  options: UseMutationOptions<AskableAsset, unknown, AskableAssetFormData> = {},
) => {
  return useMutation((data) => AskablesAPI.createAsset(askableId, data), options);
};

/**
 * Mutation to delete an AskableAsset.
 */
export const useAskableAssetDelete = (options: UseMutationOptions<void, unknown, number> = {}) => {
  const queryClient = useQueryClient();
  return useMutation((id) => AskablesAPI.deleteAsset(id), {
    ...options,
    onSuccess(data, variables, context) {
      queryClient.removeQueries(queryKeys.asset(variables));
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to download an AskableAsset.
 */
export const useAskableAssetDownload = (options: UseMutationOptions<void, unknown, number> = {}) => {
  return useMutation((id) => AskablesAPI.downloadAsset(id), options);
};

/**
 * Copy html for an AskableAsset.
 */
export const useAskableAssetCopy = (options: UseMutationOptions<string, unknown, number> = {}) => {
  return useMutation((id) => AskablesAPI.copyAsset(id), options);
};

export const useAskableImage = (
  id: number,
  params: AskableImageParams,
  options: UseQueryOptions<Blob, unknown, Blob> = {},
) => {
  return useQuery<Blob, unknown, Blob>(queryKeys.image(id, params), () => AskablesAPI.getImage(id, params), options);
};
